import { create } from "zustand";
import { createJSONStorage, devtools, persist } from "zustand/middleware";
import { apiService } from "../services/api";
import type { VehicleCategory } from "../types/vehicleCategory";
import { isTimestampWithinWindow } from "../utils/helpers";
type CategoryQueryParams = Parameters<
  typeof apiService.getVehicleCategories
>[0];

interface VehicleCategoryState {
  categories: VehicleCategory[];
  loading: boolean;
  isRefreshing: boolean;
  hasLoaded: boolean;
  lastFetchedAt: string | null;
  totalCount: number;
  currentPage: number;
  totalPages: number;
  itemsPerPage: number;
  fetchCategories: (params?: Record<string, unknown>) => Promise<void>;
  loadIfNeeded: () => Promise<void>;
  refreshCategories: () => Promise<void>;
  addCategory: (
    category: Omit<VehicleCategory, "created_at" | "updated_at">,
  ) => Promise<void>;
  updateCategory: (
    categoryId: string,
    updates: Partial<
      Pick<
        VehicleCategory,
        | "category_id"
        | "category_name"
        | "marker_image"
        | "color"
        | "max_speed_kmh"
      >
    >,
  ) => Promise<void>;
  removeCategory: (categoryId: string) => Promise<void>;
  getCategoryById: (categoryId?: string | null) => string | null;
}

export const useVehicleCategoryStore = create<VehicleCategoryState>()(
  devtools(
    persist(
      (set, get) => ({
        categories: [],
        loading: false,
        isRefreshing: false,
        hasLoaded: false,
        lastFetchedAt: null,
        totalCount: 0,
        currentPage: 1,
        totalPages: 1,
        itemsPerPage: 20,

        fetchCategories: async (params = { page: 1, limit: 20 }) => {
          set({ loading: true });
          try {
            const response = (await apiService.getVehicleCategories(
              params as CategoryQueryParams,
            )) as {
              categories: VehicleCategory[];
              pagination?: {
                total: number;
                page: number;
                limit: number;
                totalPages: number;
              };
            };

            const pagination = response.pagination || {
              total: response.categories.length,
              page: 1,
              limit: response.categories.length,
              totalPages: 1,
            };

            set({
              categories: response.categories,
              totalCount: pagination.total,
              currentPage: pagination.page,
              totalPages: pagination.totalPages,
              itemsPerPage: pagination.limit,
              hasLoaded: true,
              lastFetchedAt: new Date().toISOString(),
              loading: false,
            });
          } catch (error) {
            set({ loading: false });
            throw error;
          }
        },

        loadIfNeeded: async () => {
          const {
            hasLoaded,
            categories,
            isRefreshing,
            loading,
            lastFetchedAt,
            refreshCategories,
          } = get();
          if (isRefreshing || loading) {
            return;
          }
          if (
            hasLoaded &&
            categories.length > 0 &&
            isTimestampWithinWindow(lastFetchedAt, 60 * 60 * 1000, Date.now())
          ) {
            return;
          }
          await refreshCategories();
        },

        refreshCategories: async () => {
          set({ loading: true, isRefreshing: true });
          try {
            const response = (await apiService.getAllVehicleCategories({
              sort_by: "category_name",
              sort_order: "asc",
            })) as {
              categories: VehicleCategory[];
            };
            set({
              categories: response.categories,
              totalCount: response.categories.length,
              currentPage: 1,
              totalPages: 1,
              itemsPerPage: response.categories.length || 1,
              hasLoaded: true,
              lastFetchedAt: new Date().toISOString(),
              loading: false,
              isRefreshing: false,
            });
          } catch (error) {
            set({ loading: false, isRefreshing: false });
            throw error;
          }
        },

        addCategory: async (category) => {
          const created = (await apiService.createVehicleCategory(
            category,
          )) as VehicleCategory;
          set((state) => ({
            categories: [created, ...state.categories],
            totalCount: state.totalCount + 1,
          }));
        },

        updateCategory: async (categoryId, updates) => {
          const updated = (await apiService.updateVehicleCategory(
            categoryId,
            updates,
          )) as VehicleCategory;

          set((state) => ({
            categories: state.categories.map((c) =>
              c.category_id === categoryId ? { ...c, ...updated } : c,
            ),
          }));
        },

        removeCategory: async (categoryId) => {
          await apiService.deleteVehicleCategory(categoryId);
          set((state) => {
            const categories = state.categories.filter(
              (c) => c.category_id !== categoryId,
            );
            return { categories, totalCount: categories.length };
          });
        },

        getCategoryById: (categoryId?: string | null) => {
          if (!categoryId) {
            return "-";
          }
          const category = get().categories.find(
            (c) => c.category_id === categoryId,
          );
          return category ? category.category_name : (categoryId ?? "-");
        },
      }),
      {
        name: "vehicle-category-store",
        storage: createJSONStorage(() => localStorage),
        partialize: (state) => ({
          categories: state.categories,
          hasLoaded: state.hasLoaded,
          lastFetchedAt: state.lastFetchedAt,
          totalCount: state.totalCount,
        }),
      },
    ),
    { name: "vehicle-category-store-devtools" },
  ),
);
